<template>
  <div id="app">
    <component :is="layout">
      <transition name="fade" mode="out-in">
        <router-view />
      </transition>
    </component>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import {
  INACTIVE_USER_TIME_THRESHOLD,
  USER_ACTIVITY_THROTTLER_TIME
} from "../constants";

const default_layout = "default";

export default {
  data() {
    return {
      isInactive: false,
      userActivityThrottlerTimeout: null,
      userActivityTimeout: null,
      protectedRoutes: [
        "/",
        "/expenses",
        "/reports",
        "/patients",
        "/services",
        "/charges",
        "/lookups",
        "/settings",
        "/business-details",
        "/templates",
        "/finance",
        "/doctor-reports"
      ],
      hasRunTemplateReportsList: false
    };
  },
  methods: {
    ...mapActions({
      getPermissions: "bussiness/getPermissions",
      templateReportsList: "reports/templateReportsList",
      fetchBranch: "expenses/fetchBranch"
    }),
    ...mapActions("auth", ["checkUpdateNotification"]),

    activateActivityTracker() {
      window.addEventListener("mousemove", this.userActivityThrottler);
      window.addEventListener("mousedown", this.userActivityThrottler);
      window.addEventListener("scroll", this.userActivityThrottler);
      window.addEventListener("keydown", this.userActivityThrottler);
      window.addEventListener("resize", this.userActivityThrottler);
      window.addEventListener("touchmove", this.userActivityThrottler);
      window.addEventListener("onscroll", this.userActivityThrottler);
    },

    deactivateActivityTracker() {
      window.removeEventListener("mousemove", this.userActivityThrottler);
      window.removeEventListener("mousedown", this.userActivityThrottler);
      window.removeEventListener("scroll", this.userActivityThrottler);
      window.removeEventListener("keydown", this.userActivityThrottler);
      window.removeEventListener("resize", this.userActivityThrottler);
      window.removeEventListener("touchmove", this.userActivityThrottler);
      window.removeEventListener("onscroll", this.userActivityThrottler);
    },

    resetUserActivityTimeout() {
      clearTimeout(this.userActivityTimeout);

      // Skip activity timeout if Remember Me is enabled
      const rememberMe = localStorage.getItem("rememberMe") === "true";
      if (rememberMe) {
        return;
      }

      // Skip activity timeout if inactivityTimeout is null (unlimited for user ID 1)
      if (this.inactivityTimeout === null) {
        return;
      }

      this.userActivityTimeout = setTimeout(() => {
        this.userActivityThrottler();
        if (this.isLoggedIn) {
          this.inactiveUserAction();
        }
      }, this.inactivityTimeout);
    },

    userActivityThrottler() {
      if (this.isInactive) {
        this.isInactive = false;
      }

      if (!this.userActivityThrottlerTimeout) {
        this.userActivityThrottlerTimeout = setTimeout(() => {
          this.resetUserActivityTimeout();
          clearTimeout(this.userActivityThrottlerTimeout);
          this.userActivityThrottlerTimeout = null;
        }, USER_ACTIVITY_THROTTLER_TIME);
      }
    },

    inactiveUserAction() {
      this.isInactive = true;
    },

    async storeBranchData() {
      await this.fetchBranch();
      if (this.fetchUserBranch) {
        const branchData = this.fetchUserBranch.map(elem => ({
          name: elem.name,
          key: elem.id
        }));
        localStorage.setItem("branchData", JSON.stringify(branchData));
      }
    }
  },
  computed: {
    ...mapGetters({
      userRole: "auth/role",
      isLoggedIn: "auth/isLoggedIn",
      userInfo: "auth/userInfo",
      fetchUserBranch: "expenses/fetchUserBranch"
    }),
    layout() {
      return this.$route.meta.layout || default_layout + "-layout";
    },
    inactivityTimeout() {
      // If user ID is 1, unlimited timeout (null), otherwise use default 10 minutes
      return this.userInfo && this.userInfo.id === 1
        ? null // unlimited
        : INACTIVE_USER_TIME_THRESHOLD; // 10 minutes
    }
  },
  mounted() {
    const role = this.userRole && this.userRole.map(item => item.name);
    if (
      role &&
      role.includes("receptionist") &&
      this.protectedRoutes.includes(location.pathname)
    ) {
      this.$router.push("/registration/register");
    } else if (
      role &&
      role.includes("technician") &&
      this.protectedRoutes.includes(location.pathname)
    ) {
      this.$router.push("/registration/register");
    }
    if (this.isLoggedIn) {
      console.log("User is logged in, checking for notifications...");
      this.checkUpdateNotification();
      // add events after user logged in only if Remember Me is not enabled
      const rememberMe = localStorage.getItem("rememberMe") === "true";
      if (!rememberMe) {
        this.activateActivityTracker();
      }
      // Run once after login for template reports
      if (!localStorage.getItem("hasRunTemplateReportsList")) {
        this.templateReportsList("update");
        localStorage.setItem("hasRunTemplateReportsList", "true");
      }

      // Run once after login for branch data
      if (!localStorage.getItem("hasStoredBranchData")) {
        this.storeBranchData();
        localStorage.setItem("hasStoredBranchData", "true");
      }
    } else {
      // destroy events after logout
      this.deactivateActivityTracker();
      clearTimeout(this.userActivityTimeout);
      clearTimeout(this.userActivityThrottlerTimeout);
    }
  },
  watch: {
    isInactive(val) {
      // Skip inactivity logout if Remember Me is enabled
      const rememberMe = localStorage.getItem("rememberMe") === "true";
      if (
        val &&
        this.isLoggedIn &&
        !rememberMe &&
        process.env.NODE_ENV !== "development"
      ) {
        this.$toasted.show("You are going to be logout soon").goAway("2000");
        setTimeout(() => {
          this.$store.dispatch("auth/logout", { vm: this }).then(() => {
            this.$router.push("/");
          });
        }, 3000);
      }
    },
    $route(to) {
      document.title = to.meta.title ? `${to.meta.title} - UCEMR` : "UCEMR";
    }
  }
};
</script>

<style>
/* animation */
.fade-enter-active,
.fade-leave-active {
  transition-property: opacity;
  transition-duration: 0.3s;
}

.fade-enter-active {
  transition-delay: 0.3s;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}
</style>
